/**
 * Fuzzy Search - Sublime Text-style matching
 *
 * Characters must appear in order, but not consecutively.
 * Scoring rewards: consecutive matches, word boundaries, camel case.
 * Optimized for performance with pre-computed lowercase and char code checks.
 */

export interface SearchableItem {
  id: string;
  type: "task" | "fact" | "rule" | "activity";
  text: string;
  textLower: string; // Pre-computed lowercase for performance
  secondary?: string;
  secondaryLower?: string; // Pre-computed lowercase
  status?: string;
  refs?: string[];
  refsLower?: string[]; // Pre-computed lowercase
}

export interface FuzzyResult {
  item: SearchableItem;
  score: number;
  matches: number[];
}

// Character codes for word boundary detection (faster than regex)
const SPACE = 32, UNDERSCORE = 95, DASH = 45, DOT = 46, SLASH = 47;

function isWordBoundary(charCode: number): boolean {
  return charCode === SPACE || charCode === UNDERSCORE ||
         charCode === DASH || charCode === DOT || charCode === SLASH;
}

function isLowerCase(char: string): boolean {
  const code = char.charCodeAt(0);
  return code >= 97 && code <= 122;
}

function isUpperCase(char: string): boolean {
  const code = char.charCodeAt(0);
  return code >= 65 && code <= 90;
}

/**
 * Fuzzy match a query against pre-lowercased text
 * Returns null if no match, otherwise returns score and match indices
 */
export function fuzzyMatch(
  queryLower: string,
  textLower: string,
  textOriginal: string
): { score: number; matches: number[] } | null {
  const queryLen = queryLower.length;
  const textLen = textLower.length;

  // Quick rejection: query longer than text
  if (queryLen > textLen) return null;

  let score = 0;
  let queryIdx = 0;
  let prevMatchIdx = -2;
  const matches: number[] = [];

  for (let i = 0; i < textLen && queryIdx < queryLen; i++) {
    if (textLower.charCodeAt(i) === queryLower.charCodeAt(queryIdx)) {
      matches.push(i);

      // Consecutive match bonus
      if (prevMatchIdx === i - 1) {
        score += 5;
      }

      // Word boundary bonus (start of word) - using char codes
      if (i === 0 || isWordBoundary(textOriginal.charCodeAt(i - 1))) {
        score += 10;
      }

      // Camel case boundary bonus
      if (i > 0 && isUpperCase(textOriginal[i]) && isLowerCase(textOriginal[i - 1])) {
        score += 8;
      }

      // Exact case match bonus
      if (queryLower.charCodeAt(queryIdx) === textOriginal.charCodeAt(i)) {
        score += 1;
      }

      prevMatchIdx = i;
      queryIdx++;
      score += 1;
    }
  }

  // All query characters must match
  if (queryIdx !== queryLen) {
    return null;
  }

  // Prefer shorter strings (tighter match)
  score -= textLen * 0.1;

  // Bonus for match starting at beginning
  if (matches[0] === 0) {
    score += 15;
  }

  return { score, matches };
}

/**
 * Search items with fuzzy matching
 * Returns sorted results by score (highest first)
 */
export function fuzzySearch(query: string, items: SearchableItem[], limit = 50): FuzzyResult[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  // Pre-compute query lowercase once
  const queryLower = trimmedQuery.toLowerCase();
  const results: FuzzyResult[] = [];

  for (const item of items) {
    // Try primary text (uses pre-computed lowercase)
    const primaryMatch = fuzzyMatch(queryLower, item.textLower, item.text);
    if (primaryMatch) {
      results.push({
        item,
        score: primaryMatch.score,
        matches: primaryMatch.matches,
      });
      continue;
    }

    // Try secondary text if exists (uses pre-computed lowercase)
    if (item.secondaryLower && item.secondary) {
      const secondaryMatch = fuzzyMatch(queryLower, item.secondaryLower, item.secondary);
      if (secondaryMatch) {
        results.push({
          item,
          score: secondaryMatch.score - 5, // Slight penalty for secondary match
          matches: [], // Don't highlight secondary matches in primary text
        });
        continue;
      }
    }

    // Try refs with fuzzy matching (uses pre-computed lowercase)
    if (item.refs && item.refsLower) {
      for (let i = 0; i < item.refs.length; i++) {
        const refMatch = fuzzyMatch(queryLower, item.refsLower[i], item.refs[i]);
        if (refMatch) {
          results.push({
            item,
            score: refMatch.score + 50, // Bonus for ref match
            matches: [],
          });
          break;
        }
      }
    }
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results.slice(0, limit);
}

/**
 * Extract external references from text (Jira, GitHub, URLs)
 */
export function extractRefs(text: string): string[] {
  const refs: string[] = [];

  // Jira/Linear style: PROJ-1234
  const jiraMatches = text.match(/\b[A-Z][A-Z0-9]+-\d+\b/g);
  if (jiraMatches) refs.push(...jiraMatches);

  // GitHub issues: #42
  const issueMatches = text.match(/#\d+\b/g);
  if (issueMatches) refs.push(...issueMatches);

  // URLs
  const urlMatches = text.match(/https?:\/\/[^\s)"']+/g);
  if (urlMatches) refs.push(...urlMatches);

  return [...new Set(refs)]; // Dedupe
}

/**
 * Build search index from plan entries and tasks
 * Uses parsed task metadata from the Spindle engine
 * Pre-computes lowercase strings for fast searching
 */
export function buildSearchIndex(
  entries: Array<{ seq: number; forms: string[] }>,
  tasks: Array<{ id: string; status: string; meta?: Record<string, string | string[] | undefined> }>
): SearchableItem[] {
  const items: SearchableItem[] = [];
  const seenTasks = new Set<string>();

  // Helper to create item with pre-computed lowercase
  function createItem(
    id: string,
    type: SearchableItem["type"],
    text: string,
    secondary?: string,
    status?: string,
    refs?: string[]
  ): SearchableItem {
    return {
      id,
      type,
      text,
      textLower: text.toLowerCase(),
      secondary,
      secondaryLower: secondary?.toLowerCase(),
      status,
      refs: refs && refs.length > 0 ? refs : undefined,
      refsLower: refs && refs.length > 0 ? refs.map(r => r.toLowerCase()) : undefined,
    };
  }

  // Index tasks with their metadata from the Spindle parser
  for (const task of tasks) {
    seenTasks.add(task.id);
    const description = task.meta?.description as string | undefined;
    const source = task.meta?.source as string | undefined;

    // Collect all string values from meta as potential searchable text
    const metaValues: string[] = [];
    if (task.meta) {
      for (const value of Object.values(task.meta)) {
        if (typeof value === "string") metaValues.push(value);
        if (Array.isArray(value)) metaValues.push(...value.filter(v => typeof v === "string"));
      }
    }

    // Extract refs from all meta values
    const refs = extractRefs(metaValues.join(" "));

    // Also add source directly as a ref if it looks like a ticket ID
    if (source && !refs.includes(source.toUpperCase())) {
      refs.push(source.toUpperCase());
    }

    items.push(createItem(task.id, "task", task.id, description, task.status, refs));
  }

  // Index forms from entries
  for (const entry of entries) {
    for (const form of entry.forms) {
      // Skip meta forms (already captured in tasks)
      if (form.startsWith("(meta ")) continue;

      // Parse given facts
      const givenMatch = form.match(/\(given\s+([a-zA-Z0-9_-]+)\)/);
      if (givenMatch) {
        const factName = givenMatch[1];
        // Skip task-related facts we've already indexed
        if (factName.startsWith("task-") && seenTasks.has(factName.replace("task-", ""))) continue;
        if (factName.startsWith("completed-") || factName.startsWith("claimed-") || factName.startsWith("blocked-")) continue;
        // Skip external reference facts (should be found via task refs instead)
        if (factName.match(/^(jira|linear|github|slack|url)-/i)) continue;
        // Skip dependency facts
        if (factName.startsWith("no-deps-") || factName.startsWith("deps-met-")) continue;

        items.push(createItem(`fact-${factName}`, "fact", factName, undefined, undefined, extractRefs(form)));
        continue;
      }

      // Parse rules
      const ruleMatch = form.match(/\(normally\s+([a-zA-Z0-9_-]+)\s+/);
      if (ruleMatch) {
        items.push(createItem(`rule-${ruleMatch[1]}`, "rule", ruleMatch[1], form.slice(0, 100)));
        continue;
      }

      // Parse claims with notes
      const claimMatch = form.match(/\(claims\s+"([^"]+)"[^)]*:note\s+"([^"]+)"/);
      if (claimMatch) {
        const refs = extractRefs(claimMatch[2]);
        items.push(createItem(
          `claim-${entry.seq}-${claimMatch[1]}`,
          "activity",
          claimMatch[2],
          `by ${claimMatch[1]}`,
          undefined,
          refs
        ));
      }
    }
  }

  return items;
}
